import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import {
  clearAllWishlistCache,
  loadFromCache,
  saveToCache
} from '@/shared/composables/wishlist/wishlist.cache';
import {
  findMatchingItem,
  hasMatchingItem
} from '@/shared/composables/wishlist/wishlist.matchers';
import {
  fetchProductDataForItems as fetchProductData,
  preloadImages,
  shouldFetchProductData
} from '@/shared/composables/wishlist/wishlist.productData';
import {
  CONCURRENT_FETCH_TIMEOUT_MS,
  type ProductData,
  STORAGE_KEY_IMAGES,
  STORAGE_KEY_ITEMS,
  STORAGE_KEY_PRODUCT_DATA,
  STORAGE_KEY_WISHLISTS,
  type Wishlist,
  type WishlistItemWithWishlistId,
  WISHLISTS_PAGE_SIZE
} from '@/shared/composables/wishlist/wishlist.types';
import {
  MULTI_WISHLIST_ITEMS_LIMIT,
  WishlistApiService
} from '@/shared/services/wishlistAPI';

import { useUserStore } from './user';

// Re-export types for external consumers
export type { ProductData, Wishlist, WishlistItemWithWishlistId };

export const useWishlistStore = defineStore('wishlist', () => {
  // State
  const _wishlistItems = ref<WishlistItemWithWishlistId[]>([]);
  const _wishlists = ref<Wishlist[]>([]);
  const _loading = ref(false);
  const _loadingMore = ref(false);
  const _dataLoaded = ref(false);
  const _hasCachedData = ref(false);
  const _imageByItemId = ref<Record<string, string>>({});
  const _productDataByItemId = ref<Record<string, ProductData>>({});
  const _fetchPromise = ref<Promise<void> | null>(null);
  const _wishlistsCursor = ref<string | null>(null);
  const _wishlistsHasMore = ref(false);

  // Counter for generating unique optimistic IDs (prevents collisions when called in quick succession)
  let _optimisticIdCounter = 0;

  // Computed
  const wishlistItems = computed(() => _wishlistItems.value);
  const wishlists = computed(() => _wishlists.value);
  const loading = computed(() => _loading.value);
  const loadingMore = computed(() => _loadingMore.value);
  const wishlistCount = computed(() => _wishlistItems.value.length);
  const totalWishlistsCount = computed(() => _wishlists.value.length);
  const dataLoaded = computed(() => _dataLoaded.value);
  const imageByItemId = computed(() => _imageByItemId.value);
  const productDataByItemId = computed(() => _productDataByItemId.value);
  const wishlistsHasMore = computed(() => _wishlistsHasMore.value);

  const defaultWishlist = computed(() => {
    return (
      _wishlists.value.find((w) => w.is_default) || _wishlists.value[0] || null
    );
  });

  // Cache Operations
  const saveCachedWishlists = () => {
    saveToCache(STORAGE_KEY_WISHLISTS, _wishlists.value);
  };

  const loadCachedWishlists = (): boolean => {
    const cached = loadFromCache<Wishlist[]>(STORAGE_KEY_WISHLISTS);
    if (cached && Array.isArray(cached)) {
      _wishlists.value = cached;
      return true;
    }
    return false;
  };

  const loadCachedItems = (): boolean => {
    const cached =
      loadFromCache<WishlistItemWithWishlistId[]>(STORAGE_KEY_ITEMS);
    if (cached && Array.isArray(cached)) {
      _wishlistItems.value = cached;
      _hasCachedData.value = true;
      return true; // Indicate that cached data was loaded
    }
    return false;
  };

  const saveCachedItems = () => {
    saveToCache(STORAGE_KEY_ITEMS, _wishlistItems.value);
  };

  const loadCachedImages = () => {
    const cachedImages =
      loadFromCache<Record<string, string>>(STORAGE_KEY_IMAGES);
    if (cachedImages) {
      _imageByItemId.value = cachedImages;
    }

    const cachedProductData = loadFromCache<Record<string, ProductData>>(
      STORAGE_KEY_PRODUCT_DATA
    );
    if (cachedProductData) {
      _productDataByItemId.value = cachedProductData;
    }
  };

  const saveCachedImages = () => {
    saveToCache(STORAGE_KEY_IMAGES, _imageByItemId.value);
    saveToCache(STORAGE_KEY_PRODUCT_DATA, _productDataByItemId.value);
  };

  const cleanupOrphanedCacheEntries = () => {
    const currentItemIds = new Set(_wishlistItems.value.map((item) => item.id));

    const imageKeys = Object.keys(_imageByItemId.value);
    let hasOrphanedImages = false;
    for (const key of imageKeys) {
      if (!currentItemIds.has(key)) {
        delete _imageByItemId.value[key];
        hasOrphanedImages = true;
      }
    }

    const productDataKeys = Object.keys(_productDataByItemId.value);
    let hasOrphanedProductData = false;
    for (const key of productDataKeys) {
      if (!currentItemIds.has(key)) {
        delete _productDataByItemId.value[key];
        hasOrphanedProductData = true;
      }
    }

    if (hasOrphanedImages || hasOrphanedProductData) {
      saveCachedImages();
    }
  };

  // Product Data Fetching
  const fetchWishlistImages = async (forceRefresh = false) => {
    const items = _wishlistItems.value;
    if (!items.length) return;

    // Skip fetching only if not force refreshing and all items have cached images
    if (!forceRefresh && !shouldFetchProductData(items, _imageByItemId.value)) {
      return;
    }

    const result = await fetchProductData(items);

    _imageByItemId.value = {
      ..._imageByItemId.value,
      ...result.imageByItemId
    };
    _productDataByItemId.value = {
      ..._productDataByItemId.value,
      ...result.productDataByItemId
    };

    const imageUrls = Object.values(result.imageByItemId).filter(Boolean);
    preloadImages(imageUrls, 3);
    saveCachedImages();
  };

  const getImageUrlFor = (id: string) => imageByItemId.value[id] || '';

  const getProductDataFor = (id: string): ProductData | null => {
    return productDataByItemId.value[id] || null;
  };

  const fetchProductDataForItems = async (
    items: Array<{
      id: string;
      product_group_identifier?: string | null;
      sku?: string | null;
    }>
  ) => {
    if (!items.length) return;

    const result = await fetchProductData(items);

    _imageByItemId.value = {
      ..._imageByItemId.value,
      ...result.imageByItemId
    };
    _productDataByItemId.value = {
      ..._productDataByItemId.value,
      ...result.productDataByItemId
    };

    const imageUrls = Object.values(result.imageByItemId).filter(Boolean);
    preloadImages(imageUrls, 3);
    saveCachedImages();
  };

  // Wishlist Fetching
  const fetchWishlist = async (forceRefresh = false) => {
    if (_dataLoaded.value && !forceRefresh) {
      return;
    }

    if (_fetchPromise.value) {
      try {
        await Promise.race([
          _fetchPromise.value,
          new Promise<void>((_, reject) =>
            setTimeout(
              () => reject(new Error('Fetch timeout exceeded')),
              CONCURRENT_FETCH_TIMEOUT_MS
            )
          )
        ]);
      } catch {
        // Timeout or error - continue with new fetch
      }

      if (_dataLoaded.value && !forceRefresh) {
        return;
      }
    }

    const fetchPromise = (async () => {
      _loading.value = true;

      // Clear all cached product data when force refreshing to ensure fresh state
      if (forceRefresh) {
        _imageByItemId.value = {};
        _productDataByItemId.value = {};
        clearAllWishlistCache();
      }

      try {
        _wishlistsCursor.value = null;
        _wishlistsHasMore.value = false;

        // Fetch ALL wishlists by paginating through all pages
        // This ensures consistent behavior regardless of how many wishlists the user has
        const allFetchedWishlists: Wishlist[] = [];
        let cursor: string | null = null;
        let hasMore = true;

        while (hasMore) {
          const wishlistsResponse =
            await WishlistApiService.fetchMultiWishlists(
              'updated_at',
              'desc',
              cursor,
              WISHLISTS_PAGE_SIZE
            );
          const fetchedWishlists = wishlistsResponse?.wishlists || [];
          allFetchedWishlists.push(...fetchedWishlists);

          cursor = wishlistsResponse?.cursor || null;
          hasMore = !!cursor;
        }

        // After fetching all, there's no more to load
        _wishlistsCursor.value = null;
        _wishlistsHasMore.value = false;

        _wishlists.value = allFetchedWishlists;

        const allWishlistItems: WishlistItemWithWishlistId[] = [];
        const seenItems = new Set<string>();

        for (const wishlist of allFetchedWishlists) {
          const items = wishlist.items?.items || [];
          for (const item of items) {
            const uniqueKey = `${wishlist.id}-${item.product_group_identifier || ''}-${item.sku || ''}`;
            if (!seenItems.has(uniqueKey)) {
              seenItems.add(uniqueKey);
              allWishlistItems.push({
                __typename: 'WishlistItem',
                id: item.id,
                created_at: item.created_at,
                product_group_identifier: item.product_group_identifier,
                sku: item.sku,
                wishlistId: wishlist.id
              } as WishlistItemWithWishlistId);
            }
          }
        }

        _wishlistItems.value = allWishlistItems;
        _dataLoaded.value = true;
        saveCachedItems();
        saveCachedWishlists();

        if (allWishlistItems.length > 0) {
          await fetchWishlistImages(forceRefresh);
        }
      } finally {
        _loading.value = false;
        _fetchPromise.value = null;
      }
    })();

    _fetchPromise.value = fetchPromise;
    await fetchPromise;
  };

  // Note: This function is kept for backward compatibility but is essentially a no-op
  // since fetchWishlist now loads ALL wishlists upfront (_wishlistsHasMore is always false)
  const fetchMoreWishlists = async () => {
    if (
      _loadingMore.value ||
      !_wishlistsHasMore.value ||
      !_wishlistsCursor.value
    ) {
      return;
    }

    _loadingMore.value = true;

    try {
      const wishlistsResponse = await WishlistApiService.fetchMultiWishlists(
        'updated_at',
        'desc',
        _wishlistsCursor.value,
        WISHLISTS_PAGE_SIZE
      );
      const fetchedWishlists = wishlistsResponse?.wishlists || [];

      _wishlistsCursor.value = wishlistsResponse?.cursor || null;
      _wishlistsHasMore.value = !!wishlistsResponse?.cursor;
      _wishlists.value = [..._wishlists.value, ...fetchedWishlists];

      const newWishlistItems: WishlistItemWithWishlistId[] = [];
      const existingItemKeys = new Set(
        _wishlistItems.value.map(
          (item) =>
            `${item.wishlistId}-${item.product_group_identifier || ''}-${item.sku || ''}`
        )
      );

      for (const wishlist of fetchedWishlists) {
        const items = wishlist.items?.items || [];
        for (const item of items) {
          const uniqueKey = `${wishlist.id}-${item.product_group_identifier || ''}-${item.sku || ''}`;
          if (!existingItemKeys.has(uniqueKey)) {
            existingItemKeys.add(uniqueKey);
            newWishlistItems.push({
              __typename: 'WishlistItem',
              id: item.id,
              created_at: item.created_at,
              product_group_identifier: item.product_group_identifier,
              sku: item.sku,
              wishlistId: wishlist.id
            } as WishlistItemWithWishlistId);
          }
        }
      }

      if (newWishlistItems.length > 0) {
        _wishlistItems.value = [..._wishlistItems.value, ...newWishlistItems];
      }

      saveCachedItems();
      saveCachedWishlists();

      if (newWishlistItems.length > 0) {
        await fetchWishlistImages();
      }
    } finally {
      _loadingMore.value = false;
    }
  };

  // Item CRUD Operations
  const addToWishlist = async (
    wishlistId: string,
    productGroupIdentifier?: string,
    sku?: string
  ) => {
    // Generate unique ID using counter + timestamp + random to prevent collisions
    // when called multiple times in quick succession
    _optimisticIdCounter += 1;
    const uniqueId = `temp-${Date.now()}-${_optimisticIdCounter}-${Math.random().toString(36).substring(2, 9)}`;

    const optimisticItem: WishlistItemWithWishlistId = {
      id: uniqueId,
      created_at: new Date().toISOString(),
      product_group_identifier: productGroupIdentifier || null,
      sku: sku || null,
      __typename: 'WishlistItem' as const,
      wishlistId
    };

    _wishlistItems.value = [..._wishlistItems.value, optimisticItem];

    _wishlists.value = _wishlists.value.map((wishlist) => {
      if (wishlist.id === wishlistId && wishlist.items) {
        return {
          ...wishlist,
          items: {
            ...wishlist.items,
            items: [...(wishlist.items.items || []), optimisticItem]
          }
        };
      }
      return wishlist;
    });

    await new Promise((resolve) => setTimeout(resolve, 50));
    _loading.value = true;

    try {
      const wishlistItem = await WishlistApiService.addItemToWishlist(
        wishlistId,
        productGroupIdentifier,
        sku
      );

      if (wishlistItem) {
        const realItem: WishlistItemWithWishlistId = {
          ...wishlistItem,
          wishlistId
        };
        _wishlistItems.value = _wishlistItems.value.map((item) =>
          item.id === optimisticItem.id ? realItem : item
        );

        _wishlists.value = _wishlists.value.map((wishlist) => {
          if (wishlist.id === wishlistId && wishlist.items?.items) {
            return {
              ...wishlist,
              items: {
                ...wishlist.items,
                items: wishlist.items.items.map((item) =>
                  item.id === optimisticItem.id ? wishlistItem : item
                )
              }
            };
          }
          return wishlist;
        });

        saveCachedItems();
        saveCachedWishlists();
        await fetchWishlistImages();
      } else {
        // API returned null/undefined - clean up the optimistic item
        _wishlistItems.value = _wishlistItems.value.filter(
          (item) => item.id !== optimisticItem.id
        );

        _wishlists.value = _wishlists.value.map((wishlist) => {
          if (wishlist.id === wishlistId && wishlist.items?.items) {
            return {
              ...wishlist,
              items: {
                ...wishlist.items,
                items: wishlist.items.items.filter(
                  (item) => item.id !== optimisticItem.id
                )
              }
            };
          }
          return wishlist;
        });
      }

      return wishlistItem;
    } catch (error) {
      _wishlistItems.value = _wishlistItems.value.filter(
        (item) => item.id !== optimisticItem.id
      );

      _wishlists.value = _wishlists.value.map((wishlist) => {
        if (wishlist.id === wishlistId && wishlist.items?.items) {
          return {
            ...wishlist,
            items: {
              ...wishlist.items,
              items: wishlist.items.items.filter(
                (item) => item.id !== optimisticItem.id
              )
            }
          };
        }
        return wishlist;
      });

      throw error;
    } finally {
      _loading.value = false;
    }
  };

  const addToDefaultWishlist = async (
    productGroupIdentifier?: string,
    sku?: string
  ) => {
    const defaultWishlistValue = defaultWishlist.value;
    if (!defaultWishlistValue) {
      throw new Error('No default wishlist found');
    }
    return addToWishlist(defaultWishlistValue.id, productGroupIdentifier, sku);
  };

  const removeFromWishlist = async (wishlistId: string, itemId: string) => {
    const itemToRemove = _wishlistItems.value.find(
      (item) => item.id === itemId && item.wishlistId === wishlistId
    );
    _wishlistItems.value = _wishlistItems.value.filter(
      (item) => !(item.id === itemId && item.wishlistId === wishlistId)
    );

    const wishlistBackup = _wishlists.value.find((w) => w.id === wishlistId);
    _wishlists.value = _wishlists.value.map((wishlist) => {
      if (wishlist.id === wishlistId && wishlist.items?.items) {
        return {
          ...wishlist,
          items: {
            ...wishlist.items,
            items: wishlist.items.items.filter((item) => item.id !== itemId)
          }
        };
      }
      return wishlist;
    });

    const imageToRestore = _imageByItemId.value[itemId];
    const productDataToRestore = _productDataByItemId.value[itemId];
    const hadImage = itemId in _imageByItemId.value;
    const hadProductData = itemId in _productDataByItemId.value;

    if (hadImage) {
      delete _imageByItemId.value[itemId];
    }
    if (hadProductData) {
      delete _productDataByItemId.value[itemId];
    }

    _loading.value = true;

    try {
      await WishlistApiService.removeItemFromWishlist(wishlistId, itemId);

      saveCachedItems();
      saveCachedWishlists();
      if (hadImage || hadProductData) {
        saveCachedImages();
      }

      cleanupOrphanedCacheEntries();
    } catch (error) {
      if (itemToRemove) {
        _wishlistItems.value = [..._wishlistItems.value, itemToRemove];
      }

      if (wishlistBackup) {
        _wishlists.value = _wishlists.value.map((wishlist) =>
          wishlist.id === wishlistId ? wishlistBackup : wishlist
        );
      }

      if (hadImage && imageToRestore !== undefined) {
        _imageByItemId.value[itemId] = imageToRestore;
      }

      if (hadProductData && productDataToRestore !== undefined) {
        _productDataByItemId.value[itemId] = productDataToRestore;
      }

      if (itemToRemove) {
        saveCachedItems();
      }
      if (wishlistBackup) {
        saveCachedWishlists();
      }
      if (
        (hadImage && imageToRestore !== undefined) ||
        (hadProductData && productDataToRestore !== undefined)
      ) {
        saveCachedImages();
      }

      throw error;
    } finally {
      _loading.value = false;
    }
  };

  // Query Helpers
  const isInWishlist = (productGroupIdentifier?: string, sku?: string) => {
    const userStore = useUserStore();
    if (!userStore.authenticated) {
      return false;
    }

    return hasMatchingItem(_wishlistItems.value, productGroupIdentifier, sku);
  };

  const isInSpecificWishlist = (
    wishlistId: string,
    productGroupIdentifier?: string,
    sku?: string
  ) => {
    const userStore = useUserStore();
    if (!userStore.authenticated) {
      return false;
    }

    return hasMatchingItem(
      _wishlistItems.value,
      productGroupIdentifier,
      sku,
      wishlistId
    );
  };

  const getWishlistItem = (productGroupIdentifier?: string, sku?: string) => {
    return findMatchingItem(_wishlistItems.value, productGroupIdentifier, sku);
  };

  const getWishlistItemFromWishlist = (
    wishlistId: string,
    productGroupIdentifier?: string,
    sku?: string
  ) => {
    return findMatchingItem(
      _wishlistItems.value,
      productGroupIdentifier,
      sku,
      wishlistId
    );
  };

  const getItemsForWishlist = (wishlistId: string) => {
    return _wishlistItems.value.filter(
      (item) => item.wishlistId === wishlistId
    );
  };

  const getWishlistById = (wishlistId: string) => {
    return _wishlists.value.find((w) => w.id === wishlistId) || null;
  };

  // Toggle Operations
  const toggleWishlistItem = async (
    wishlistId: string,
    productGroupIdentifier?: string,
    sku?: string
  ) => {
    const existingItem = getWishlistItemFromWishlist(
      wishlistId,
      productGroupIdentifier,
      sku
    );

    if (existingItem) {
      await removeFromWishlist(wishlistId, existingItem.id);
    } else {
      await addToWishlist(wishlistId, productGroupIdentifier, sku);
    }
  };

  const toggleWishlist = async (
    productGroupIdentifier?: string,
    sku?: string
  ) => {
    const existingItem = getWishlistItem(productGroupIdentifier, sku);

    if (existingItem) {
      await removeFromWishlist(existingItem.wishlistId, existingItem.id);
    } else {
      await addToDefaultWishlist(productGroupIdentifier, sku);
    }
  };

  // Wishlist CRUD Operations
  const deleteWishlist = async (wishlistId: string) => {
    const wishlistToDelete = _wishlists.value.find((w) => w.id === wishlistId);
    const itemsToRemove = _wishlistItems.value.filter(
      (item) => item.wishlistId === wishlistId
    );

    _wishlists.value = _wishlists.value.filter((w) => w.id !== wishlistId);
    _wishlistItems.value = _wishlistItems.value.filter(
      (item) => item.wishlistId !== wishlistId
    );

    const imagesToRestore: Record<string, string> = {};
    const productDataToRestore: Record<string, ProductData> = {};

    for (const item of itemsToRemove) {
      if (item.id in _imageByItemId.value) {
        imagesToRestore[item.id] = _imageByItemId.value[item.id];
        delete _imageByItemId.value[item.id];
      }
      if (item.id in _productDataByItemId.value) {
        productDataToRestore[item.id] = _productDataByItemId.value[item.id];
        delete _productDataByItemId.value[item.id];
      }
    }

    _loading.value = true;

    try {
      await WishlistApiService.deleteWishlist(wishlistId);

      saveCachedItems();
      saveCachedWishlists();

      if (
        Object.keys(imagesToRestore).length > 0 ||
        Object.keys(productDataToRestore).length > 0
      ) {
        saveCachedImages();
      }
    } catch (error) {
      if (wishlistToDelete) {
        _wishlists.value = [..._wishlists.value, wishlistToDelete];
      }
      _wishlistItems.value = [..._wishlistItems.value, ...itemsToRemove];

      for (const [id, url] of Object.entries(imagesToRestore)) {
        _imageByItemId.value[id] = url;
      }

      for (const [id, data] of Object.entries(productDataToRestore)) {
        _productDataByItemId.value[id] = data;
      }

      throw error;
    } finally {
      _loading.value = false;
    }
  };

  const createWishlist = async (
    name: string,
    description?: string,
    isPublic?: boolean
  ) => {
    _loading.value = true;

    try {
      const newWishlist = await WishlistApiService.createWishlist(
        name,
        description,
        isPublic
      );

      if (newWishlist) {
        _wishlists.value = [newWishlist as Wishlist, ..._wishlists.value];
        saveCachedWishlists();
      }

      return newWishlist;
    } finally {
      _loading.value = false;
    }
  };

  const updateWishlist = async (
    wishlistId: string,
    input: {
      name?: string | null;
      description?: string | null;
      isPublic?: boolean | null;
    }
  ) => {
    const wishlistToUpdate = _wishlists.value.find((w) => w.id === wishlistId);
    if (!wishlistToUpdate) {
      throw new Error('Wishlist not found');
    }

    // Optimistic update
    const optimisticWishlist: Wishlist = {
      ...wishlistToUpdate,
      name: input.name ?? wishlistToUpdate.name,
      description:
        input.description !== undefined
          ? input.description
          : wishlistToUpdate.description,
      is_public: input.isPublic ?? wishlistToUpdate.is_public
    };

    _wishlists.value = _wishlists.value.map((wishlist) =>
      wishlist.id === wishlistId ? optimisticWishlist : wishlist
    );

    _loading.value = true;

    try {
      const updatedWishlist = await WishlistApiService.updateWishlist(
        wishlistId,
        {
          name: input.name,
          description: input.description,
          public: input.isPublic
        },
        undefined,
        MULTI_WISHLIST_ITEMS_LIMIT
      );

      if (updatedWishlist) {
        _wishlists.value = _wishlists.value.map((wishlist) =>
          wishlist.id === wishlistId ? (updatedWishlist as Wishlist) : wishlist
        );
        saveCachedWishlists();
      }

      return updatedWishlist;
    } catch (error) {
      // Rollback optimistic update on error
      _wishlists.value = _wishlists.value.map((wishlist) =>
        wishlist.id === wishlistId ? wishlistToUpdate : wishlist
      );
      saveCachedWishlists();
      throw error;
    } finally {
      _loading.value = false;
    }
  };

  /**
   * Ensures a wishlist exists in _wishlists. If it's already present, no-op.
   * Used when a wishlist is loaded via direct URL
   */
  const ensureWishlistInStore = (wishlist: Wishlist) => {
    if (_wishlists.value.some((w) => w.id === wishlist.id)) {
      return;
    }
    _wishlists.value = [..._wishlists.value, wishlist];
    saveCachedWishlists();
  };

  // Initialization & Cleanup
  const loadWishlistItems = async () => {
    const hadCachedItems = loadCachedItems();
    loadCachedImages();
    loadCachedWishlists();

    if (_dataLoaded.value) {
      return;
    }

    if (_loading.value) {
      return;
    }

    if (hadCachedItems || _hasCachedData.value) {
      fetchWishlist(true).catch(() => {});
    } else {
      await fetchWishlist();
    }
  };

  const clearWishlist = () => {
    _wishlistItems.value = [];
    _wishlists.value = [];
    _imageByItemId.value = {};
    _productDataByItemId.value = {};
    _dataLoaded.value = false;
    _hasCachedData.value = false;
    _loading.value = false;
    _loadingMore.value = false;
    _fetchPromise.value = null;
    _wishlistsCursor.value = null;
    _wishlistsHasMore.value = false;
    clearAllWishlistCache();
  };

  // Auth State Watcher
  const userStore = useUserStore();
  watch(
    () => userStore.authenticated,
    (isAuthenticated) => {
      if (!isAuthenticated) {
        clearWishlist();
      } else {
        loadCachedItems();
        loadCachedImages();
        loadCachedWishlists();
      }
    },
    { immediate: false }
  );

  if (userStore.authenticated) {
    loadCachedItems();
    loadCachedImages();
    loadCachedWishlists();
  }

  return {
    wishlistItems,
    wishlists,
    loading,
    loadingMore,
    wishlistCount,
    totalWishlistsCount,
    dataLoaded,
    imageByItemId,
    defaultWishlist,
    wishlistsHasMore,
    getImageUrlFor,
    getProductDataFor,
    createWishlist,
    updateWishlist,
    ensureWishlistInStore,
    deleteWishlist,
    addToWishlist,
    removeFromWishlist,
    toggleWishlistItem,
    addToDefaultWishlist,
    toggleWishlist,
    isInWishlist,
    isInSpecificWishlist,
    getWishlistItem,
    getWishlistItemFromWishlist,
    getItemsForWishlist,
    getWishlistById,
    loadWishlistItems,
    fetchWishlist,
    fetchMoreWishlists,
    fetchProductDataForItems,
    clearWishlist
  };
});
